import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight, Crown, ShieldCheck, Briefcase, Rocket, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { PLANS, getPlansForPersona, formatBRL } from '../lib/plans';
import { createCheckoutSession } from '../lib/billingClient';
import PlanBadge from '../components/PlanBadge';

/**
 * Plans — works in two contexts:
 *  - Authenticated (rendered at /app/plans inside AppShell)
 *  - Public        (rendered at /plans inside the marketing site, no AppShell)
 *
 * The `publicView` prop toggles UX details (CTAs route to /auth, no "Plano atual" badge).
 */
const Plans = ({ publicView = false }) => {
  const { profile, user } = useAuth();
  const { plan: currentPlan } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  const highlight = new URLSearchParams(location.search).get('highlight');
  const initialPersona = profile?.role || 'investor';
  const [persona, setPersona] = useState(initialPersona);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const visiblePlans = useMemo(() => getPlansForPersona(persona), [persona]);
  const isAuthed = !!user;

  const handleSelect = async (planId) => {
    const target = PLANS[planId];
    if (!target) return;

    // Public visitor (not logged in) → go to signup carrying intent
    if (!isAuthed) {
      const params = new URLSearchParams();
      params.set('signup', '1');
      params.set('role', target.persona);
      if (target.tier !== 'free') params.set('plan', planId);
      navigate(`/auth?${params.toString()}`);
      return;
    }

    // Already on this plan
    if (planId === currentPlan.id) return;

    // Free plans don't need checkout — just go to billing dashboard
    if (target.tier === 'free') {
      navigate('/app/billing');
      return;
    }

    setLoadingPlanId(planId);
    setErrorMsg('');
    try {
      const res = await createCheckoutSession({
        planId,
        userId: user.id,
        email: user.email || profile?.email,
        name: profile?.full_name || profile?.name || undefined,
      });
      if (res?.url) {
        window.location.href = res.url; // redirect to Stripe Checkout
      } else {
        throw new Error('URL de checkout não retornada.');
      }
    } catch (e) {
      setErrorMsg(e.message || 'Falha ao iniciar checkout. Tente novamente.');
      setLoadingPlanId(null);
    }
  };

  // ----- CTA label resolver (context-aware) -----
  const ctaLabel = (p) => {
    if (isAuthed && p.id === currentPlan.id) return 'Plano atual';
    if (!isAuthed && p.tier === 'free') return 'Começar grátis';
    if (!isAuthed) return `Assinar ${p.name.split(' ').slice(-1)[0]}`;
    return p.cta; // 'Fazer upgrade' / etc
  };

  return (
    <div className={`max-w-5xl mx-auto px-4 sm:px-6 pb-20 ${publicView ? 'pt-32 sm:pt-36' : 'pt-4'}`} data-testid="plans-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <span className="eyebrow">{publicView ? 'Planos GetSpark' : 'Premium'}</span>
        <h1 className="mt-3 heading-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
          Escolha o plano que <span className="gradient-text">acelera sua Spark</span>.
        </h1>
        <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-xl mx-auto">
          {publicView
            ? 'Comece grátis hoje. Faça upgrade quando precisar de mais. Sem fidelidade, cancele quando quiser.'
            : 'Sem fidelidade. Cancele quando quiser. Pagamento via cartão ou PIX em BRL.'}
        </p>
      </motion.div>

      {/* Persona switcher */}
      <div className="flex justify-center mb-8">
        <div className="glass-card rounded-full p-1 inline-flex gap-1" data-testid="plans-persona-toggle">
          {[
            { id: 'entrepreneur', label: 'Empreendedor', icon: Rocket },
            { id: 'investor', label: 'Investidor', icon: Briefcase },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setPersona(id)}
              data-testid={`plans-persona-${id}`}
              className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold flex items-center gap-2 transition-all ${
                persona === id
                  ? id === 'investor'
                    ? 'bg-gradient-to-r from-[#ffa726] to-[#ff7043] text-white'
                    : 'bg-gradient-to-r from-[#4facfe] to-[#1e90ff] text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className={`grid gap-5 ${visiblePlans.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
        {visiblePlans.map((p, i) => {
          const isCurrent = isAuthed && currentPlan.id === p.id;
          const isHighlight = highlight === p.id || (highlight === null && p.tier === 'pro');
          const isElite = p.tier === 'elite';
          return (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-3xl p-6 sm:p-7 ${
                isElite
                  ? 'glass-card-gold ring-2 ring-[#ffa726]/40 shadow-[0_20px_60px_rgba(255,167,38,0.18)]'
                  : isHighlight
                  ? 'glass-card-blue ring-2 ring-[#4facfe]/40 shadow-[0_20px_60px_rgba(79,172,254,0.18)]'
                  : 'glass-card'
              }`}
            >
              {isHighlight && !isElite && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full bg-gradient-to-r from-[#4facfe] to-[#1e90ff] text-white">
                    Mais escolhido
                  </span>
                </div>
              )}
              {isElite && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full bg-gradient-to-r from-[#ffa726] to-[#ff7043] text-white">
                    Premium
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-bold text-white">{p.name}</h3>
                {p.badge && <PlanBadge type={p.tier === 'elite' ? 'elite' : 'pro'} size="sm" />}
              </div>
              <p className="text-xs text-slate-400 mt-1">{p.headline}</p>

              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="heading-display text-3xl sm:text-4xl font-bold text-white">
                  {p.price_label}
                </span>
                <span className="text-xs text-slate-400">{p.period_label}</span>
              </div>

              <ul className="mt-6 space-y-2.5" data-testid={`plan-features-${p.id}`}>
                {p.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-200">
                    <span className={`w-4 h-4 mt-0.5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      p.accent === 'gold' || p.tier === 'elite'
                        ? 'bg-[#ffa726]/20 text-[#ffd54f]'
                        : 'bg-[#4facfe]/20 text-[#4facfe]'
                    }`}>
                      <Check size={9} strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(p.id)}
                disabled={isCurrent || loadingPlanId === p.id}
                data-testid={`plan-cta-${p.id}`}
                className={`mt-7 w-full rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                  isCurrent
                    ? 'bg-white/[0.05] text-slate-500 cursor-default'
                    : isElite
                    ? 'btn-gold'
                    : isHighlight
                    ? 'btn-primary'
                    : 'glass-card hover:bg-white/[0.06] text-white'
                } ${loadingPlanId === p.id ? 'opacity-70 cursor-wait' : ''}`}
              >
                {loadingPlanId === p.id ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Abrindo checkout...
                  </>
                ) : isCurrent ? (
                  'Plano atual'
                ) : (
                  <>
                    {ctaLabel(p)} <ArrowRight size={14} />
                  </>
                )}
              </button>
            </motion.div>
          );
        })}
      </div>

      <p className="text-center text-[11px] text-slate-500 mt-8">
        Pagamento processado pelo Stripe. Aceitamos cartão e PIX em BRL.
      </p>

      {errorMsg && (
        <div className="max-w-md mx-auto mt-4 rounded-xl border border-red-400/30 bg-red-400/[0.08] px-4 py-3 text-xs text-red-200 text-center" data-testid="plans-error">
          {errorMsg}
        </div>
      )}

      <div className="mt-6 text-center">
        {publicView ? (
          <Link
            to="/auth"
            className="text-xs text-slate-400 hover:text-white transition-colors"
            data-testid="plans-public-signin-link"
          >
            Já tem conta? Entrar →
          </Link>
        ) : (
          <Link
            to="/app/billing"
            className="text-xs text-slate-400 hover:text-white transition-colors"
            data-testid="plans-billing-link"
          >
            Gerenciar minha assinatura →
          </Link>
        )}
      </div>
    </div>
  );
};

export default Plans;
